import { setup, assign } from "xstate"
import type { OwnerOutput } from "@core/composables"

// ================================
// TYPES
// ================================

export type FormPdlEvent =
    | { type: "START" }
    | { type: "OWNER_SUBMIT"; owner: OwnerOutput }
    | { type: "PDL_NOT_EXIST"; invitation?: boolean; pdl: string }
    | {
          type: "PDL_EXIST"
          invitation?: boolean
          consentementUser?: boolean
          pdl: string
          /** Id de la source existante quand consentementUser (pour SourceAlreadyExist) */
          _id_source?: string
          /** Email titulaire = signataire/titulaire consentement → parcours SHARE (invitation, sans accès) */
          sameTitulaireEmail?: boolean
      }
    | {
          type: "CONSENT_SIGNED"
          _id_source: string
          contractName?: string
          /** Réponse `task_building` (PUT sign) pour suivi Pusher */
          taskOnboarding?: unknown
      }
    | {
          type: "INVITATION_SENT"
          _id_consentrequest: string
          url: string
          _id_source?: string
      }
    | {
          type: "ASK_SHARING_SENT"
          _id_consentrequest?: string
          url?: string
      }
    | { type: "ALREADY_EXIST" }
    | { type: "EXIST_ERROR" }
    | { type: "RETRY" }
    | { type: "BACK" }
    | { type: "UPDATE_FORM_DONE" }

export interface FormPdlContext {
    invitation?: boolean
    /** Titulaire remonté par SourceFormOwner */
    owner?: OwnerOutput
    /** Numéro PDL saisi dans PdlFormSearch */
    pdl?: string
    /** Nom du contrat */
    contractName?: string
    /** Identifiant de la source créée lors de SourceConsentSign */
    _id_source?: string
    /** Identifiant de la demande créée lors de SourceInvitationSign */
    _id_consentrequest?: string
    /** URL de la demande créée lors de SourceInvitationSign */
    url?: string
    /** Tâche onboarding bâtiment après PUT sign (`task_building`) */
    taskOnboarding?: unknown
}

// ================================
// MACHINE
// ================================

/**
 * Machine du parcours d'ajout PDL (compteur électrique).
 * idle → SourceFormOwner → PdlFormSearch → … SHARE si invitation, sans accès, email titulaire = consentement ; SIGN si invitation sans accès et email différent ; etc.
 */
export function createFormPdlMachine() {
    return setup({
        types: {} as { context: FormPdlContext; events: FormPdlEvent },
        actions: {
            /** Définit owner et invitation (client externe = invitation true) à l'envoi du titulaire */
            setOwner: assign({
                owner: ({ event }) =>
                    event.type === "OWNER_SUBMIT" ? event.owner : undefined,
                invitation: ({ event }) =>
                    event.type === "OWNER_SUBMIT"
                        ? !event.owner?.isConnectedUser
                        : undefined,
            }),
            setPdl: assign({
                pdl: ({ event }) =>
                    event.type === "PDL_NOT_EXIST" || event.type === "PDL_EXIST"
                        ? event.pdl
                        : undefined,
            }),
            setSourceIdFromExist: assign({
                _id_source: ({ event }) =>
                    event.type === "PDL_EXIST" &&
                    "_id_source" in event &&
                    event._id_source != null
                        ? event._id_source
                        : undefined,
            }),
            setConsentResult: assign({
                _id_source: ({ event }) =>
                    event.type === "CONSENT_SIGNED"
                        ? event._id_source
                        : undefined,
                contractName: ({ event }) =>
                    event.type === "CONSENT_SIGNED"
                        ? event.contractName
                        : undefined,
                taskOnboarding: ({ event }) =>
                    event.type === "CONSENT_SIGNED"
                        ? event.taskOnboarding
                        : undefined,
            }),
            setInvitationResult: assign({
                _id_consentrequest: ({ event }) =>
                    event.type === "INVITATION_SENT"
                        ? event._id_consentrequest
                        : undefined,
                url: ({ event }) =>
                    event.type === "INVITATION_SENT" ? event.url : undefined,
                _id_source: ({ event }) =>
                    event.type === "INVITATION_SENT" && event._id_source != null
                        ? event._id_source
                        : undefined,
            }),
            setAskSharingResult: assign({
                _id_consentrequest: ({ event }) =>
                    event.type === "ASK_SHARING_SENT" &&
                    event._id_consentrequest != null
                        ? event._id_consentrequest
                        : undefined,
                url: ({ event }) =>
                    event.type === "ASK_SHARING_SENT" && event.url != null
                        ? event.url
                        : undefined,
            }),
        },
    }).createMachine({
        id: "form-pdl",
        initial: "idle",
        context: {} as FormPdlContext,
        states: {
            idle: {
                on: {
                    START: "SourceFormOwner",
                },
            },
            SourceFormOwner: {
                on: {
                    OWNER_SUBMIT: {
                        target: "PdlFormSearch",
                        actions: "setOwner",
                    },
                    BACK: "idle",
                },
            },
            PdlFormSearch: {
                on: {
                    PDL_NOT_EXIST: [
                        {
                            target: "SourceInvitationSign",
                            guard: ({ event }) =>
                                event.type === "PDL_NOT_EXIST" &&
                                !!event.invitation,
                            actions: "setPdl",
                        },
                        {
                            target: "SourceConsentSign",
                            actions: "setPdl",
                        },
                    ],
                    PDL_EXIST: [
                        {
                            target: "SourceInvitationShare",
                            guard: ({ event }) =>
                                event.type === "PDL_EXIST" &&
                                !!event.invitation &&
                                !event.consentementUser &&
                                !!event.sameTitulaireEmail,
                            actions: ["setPdl", "setSourceIdFromExist"],
                        },
                        {
                            target: "SourceInvitationSign",
                            guard: ({ event }) =>
                                event.type === "PDL_EXIST" &&
                                !!event.invitation &&
                                !event.consentementUser,
                            actions: "setPdl",
                        },
                        {
                            target: "SourceAlreadyExist",
                            guard: ({ event }) =>
                                event.type === "PDL_EXIST" &&
                                !!event.consentementUser,
                            actions: ["setPdl", "setSourceIdFromExist"],
                        },
                        { target: "SourceExistError", actions: "setPdl" },
                    ],
                    BACK: "SourceFormOwner",
                },
            },
            SourceConsentSign: {
                on: {
                    CONSENT_SIGNED: {
                        target: "done",
                        actions: "setConsentResult",
                    },
                    BACK: "PdlFormSearch",
                },
            },
            SourceInvitationSign: {
                on: {
                    // NB (DEV-16347) : nom trompeur. INVITATION_SENT = invitation
                    // GÉNÉRÉE (lien créé), pas email envoyé. L'envoi réel se fait
                    // dans SourceInvitationSignSent. À renommer si on retouche le flux.
                    INVITATION_SENT: {
                        target: "SourceInvitationSignSent",
                        actions: "setInvitationResult",
                    },
                    BACK: "PdlFormSearch",
                },
            },
            SourceInvitationSignSent: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                },
            },
            SourceInvitationShare: {
                on: {
                    ASK_SHARING_SENT: {
                        target: "SourceInvitationShareSent",
                        actions: "setAskSharingResult",
                    },
                    BACK: "PdlFormSearch",
                },
            },
            SourceInvitationShareSent: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                },
            },
            SourceAlreadyExist: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                    BACK: "PdlFormSearch",
                },
            },
            SourceExistError: {
                on: {
                    RETRY: "PdlFormSearch",
                    BACK: "PdlFormSearch",
                },
            },
            done: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                },
            },
            closed: {
                type: "final",
            },
        },
    })
}

export type FormPdlMachine = ReturnType<typeof createFormPdlMachine>
